// src/lib/sat/chart-aria.ts
//
// Builds a descriptive aria-label for a QuestionChart so a screen-reader user
// gets the actual DATA — not just "image". A chart embedded in a test question
// is unanswerable without its values, so we read out the title, both axes, and
// the data points (compactly; capped so the label stays usable).

import type { ChartSpec } from './chart-spec'
import {
  normalizeChartSeries,
  scatterGroupField,
  scatterGroupValues,
  scatterPointLabelField,
} from './chart-spec'

const TYPE_WORD: Record<ChartSpec['type'], string> = {
  bar: 'Bar chart',
  histogram: 'Histogram',
  scatter: 'Scatter plot',
  line: 'Line chart',
}

const MAX_POINTS = 12

/**
 * How many individual values the label will read out before it summarises
 * instead. Counted in VALUES, not rows: a two-series chart of 10 rows is 20
 * numbers to listen to, the same burden as a 20-row single-series chart.
 *
 * There is a real tension here and this is where it is resolved. Reading 60
 * numbers aloud is its own accessibility failure; reading none makes the
 * question unanswerable, which is worse. So past the budget we do not fall
 * silent — we give the shape of each series (first, last, smallest, largest),
 * which answers most "what is the trend" questions and tells the student what
 * they cannot get from here.
 */
const MAX_VALUES = 24

/** first / last / min / max, for a series too long to read out in full. */
function summarise(xs: { x: string; y: string }[]): string {
  const nums = xs.map((p) => Number(p.y)).filter((n) => Number.isFinite(n))
  const parts = [
    `starts at ${xs[0].x}: ${xs[0].y}`,
    `ends at ${xs[xs.length - 1].x}: ${xs[xs.length - 1].y}`,
  ]
  if (nums.length === xs.length && nums.length > 0) {
    const min = Math.min(...nums)
    const max = Math.max(...nums)
    const at = (v: number) => xs[nums.indexOf(v)].x
    parts.push(`lowest ${min} at ${at(min)}`, `highest ${max} at ${at(max)}`)
  }
  return parts.join(', ')
}

function fmt(v: unknown): string {
  if (v == null) return ''
  return String(v)
}

/** The chart's short name: its kind, and its title when it has one ("Bar chart: Sales"). */
export function chartName(spec: ChartSpec): string {
  return spec.title ? `${TYPE_WORD[spec.type]}: ${spec.title}` : TYPE_WORD[spec.type]
}

export function chartAriaLabel(spec: ChartSpec): string {
  const parts: string[] = []
  parts.push(`${chartName(spec)}.`)
  parts.push(`Horizontal axis: ${spec.xAxis.label}. Vertical axis: ${spec.yAxis.label}.`)

  const data = Array.isArray(spec.data) ? spec.data : []
  const series = Array.isArray(spec.series) ? spec.series : []
  const xKey = spec.xAxis.key

  if (spec.type === 'scatter') {
    const yKey = spec.yAxis.key
    const groupField = scatterGroupField(spec)
    const groups = groupField ? scatterGroupValues(spec, groupField) : []
    // A named point is read with its name — "H (5, 55)" — so a screen-reader
    // user can answer "which student…" too.
    const labelField = scatterPointLabelField(spec)
    const pt = (d: Record<string, unknown>) =>
      `${labelField ? `${fmt(d[labelField])} ` : ''}(${fmt(d[xKey])}, ${fmt(d[yKey])})`
    if (groupField && groups.length > 1) {
      // Read out the points grouped by their category so a screen-reader user can
      // tell the series apart (the visual cue is color, which they can't see).
      parts.push(`${data.length} points in ${groups.length} groups.`)
      for (const g of groups) {
        const rows = data.filter((d) => d[groupField] === g)
        if (rows.length === 0) continue
        if (data.length <= MAX_POINTS) {
          parts.push(`${g}: ${rows.map(pt).join(', ')}.`)
        } else {
          const points = rows.map((d) => ({ x: fmt(d[xKey]), y: fmt(d[yKey]) }))
          parts.push(`${g}: ${rows.length} points; ${summarise(points)}.`)
        }
      }
    } else {
      parts.push(`${data.length} point${data.length === 1 ? '' : 's'}.`)
      if (data.length > 0 && data.length <= MAX_POINTS) {
        parts.push(`Points: ${data.map(pt).join(', ')}.`)
      } else if (data.length > MAX_POINTS) {
        // The branch the summary treatment missed: an UNGROUPED scatter over the
        // cap still said only "40 points.", which is the same "no values at all"
        // failure the other branches were fixed for.
        parts.push(summarise(data.map((d) => ({ x: fmt(d[xKey]), y: fmt(d[yKey]) }))) + '.')
      }
    }
  } else if (series.length <= 1) {
    const yKey = series[0]?.key ?? spec.yAxis.key
    if (data.length > 0 && data.length <= MAX_POINTS) {
      parts.push(`Values — ${data.map((d) => `${fmt(d[xKey])}: ${fmt(d[yKey])}`).join(', ')}.`)
    } else if (data.length > MAX_POINTS) {
      // Past the cap this used to say only "40 data points", which answers
      // nothing. Give the shape instead of silence.
      const points = data.map((d) => ({ x: fmt(d[xKey]), y: fmt(d[yKey]) }))
      parts.push(`${data.length} data points; ${summarise(points)}.`)
    } else {
      parts.push(`${data.length} data points.`)
    }
  } else {
    // Multi-series used to stop here — series names and a row count, not one
    // number. Every question that needs a value from a two-line chart was
    // unanswerable from this label.
    const named = normalizeChartSeries(spec)
    parts.push(`${named.length} series: ${named.map((s) => s.name).join(', ')}.`)
    const rows = data.length
    const withinBudget = rows * named.length <= MAX_VALUES
    for (const sr of named) {
      const points = data.map((d) => ({ x: fmt(d[xKey]), y: fmt(d[sr.key]) }))
      if (points.length === 0) continue
      parts.push(
        withinBudget
          ? `${sr.name} — ${points.map((pt) => `${pt.x}: ${pt.y}`).join(', ')}.`
          : `${sr.name} — ${rows} points; ${summarise(points)}.`,
      )
    }
  }

  // Past the budget the label is a summary; the full values are in a visually
  // hidden table that QuestionChart renders straight after the chart. Say so,
  // so a student knows the numbers between first/last/min/max are reachable.
  if (chartDataTable(spec)) parts.push('The full data is in the table that follows.')

  return parts.join(' ')
}

/** True when chartAriaLabel reads out every value (no summarising). */
export function chartLabelIsComplete(spec: ChartSpec): boolean {
  const data = Array.isArray(spec.data) ? spec.data : []
  if (spec.type === 'scatter') return data.length <= MAX_POINTS
  const series = Array.isArray(spec.series) ? spec.series : []
  if (series.length <= 1) return data.length <= MAX_POINTS
  return data.length * normalizeChartSeries(spec).length <= MAX_VALUES
}

export type ChartDataTable = { caption: string; columns: string[]; rows: string[][] }

/**
 * The chart's full data as a table, for the cases where the aria-label had to
 * summarise — or null when the label already reads every value (a second copy
 * of a dozen numbers is only more to listen to). A screen-reader user can move
 * through a table cell by cell, which a long aria-label string never allows.
 */
export function chartDataTable(spec: ChartSpec): ChartDataTable | null {
  if (chartLabelIsComplete(spec)) return null
  const data = Array.isArray(spec.data) ? spec.data : []
  const xKey = spec.xAxis.key
  const caption = `Data for the ${TYPE_WORD[spec.type].toLowerCase()}${spec.title ? `: ${spec.title}` : ''}`
  if (spec.type === 'scatter') {
    const yKey = spec.yAxis.key
    const groupField = scatterGroupField(spec)
    const grouped = !!groupField && scatterGroupValues(spec, groupField).length > 1
    const columns = [spec.xAxis.label || xKey, spec.yAxis.label || yKey]
    if (grouped) columns.push(groupField!)
    const rows = data.map((d) => {
      const r = [fmt(d[xKey]), fmt(d[yKey])]
      if (grouped) r.push(fmt(d[groupField!]))
      return r
    })
    return { caption, columns, rows }
  }
  const named = normalizeChartSeries(spec)
  const keys = named.length ? named : [{ key: spec.yAxis.key, name: spec.yAxis.label }]
  const columns = [
    spec.xAxis.label || xKey,
    ...keys.map((k) => (keys.length === 1 ? spec.yAxis.label || k.name : k.name)),
  ]
  const rows = data.map((d) => [fmt(d[xKey]), ...keys.map((k) => fmt(d[k.key]))])
  return { caption, columns, rows }
}
